
import {
  buildApiUrl,
  sendPostRequest,
  sendPatchRequest,
  sendGetRequest,
  sendDeleteRequest,
  Type
} from '@/api/api'
import { assertSchema, assertSchemaAndReturn } from '@/helpers/assert-schema'
import {
  AssetSummaryForCreditNoteSchema,
  customerSchema,
  CreateCreditNoteRequestSchema,
  CreateCreditNoteResponseSchema,
  UpdateCreditNoteRequestSchema,
  UpdateCreditNoteResponseSchema,
  AssetVatSchema,
  type AssetVat,
  type CreateCreditNoteRequest,
  type CreateCreditNoteResponse,
  type UpdateCreditNoteRequest,
  type UpdateCreditNoteResponse,
  type GetAssetDetailsResponse,
  type CreditNoteCustomer
} from '@/api/billing/credit-note-schemas'

export async function getAssetDetailsForCreditNote(assetCode: string): Promise<GetAssetDetailsResponse> {
  const assetUrl = buildApiUrl(Type.AssetManagement, `asset/${assetCode}`)
  const { data: assetRaw } = await sendGetRequest(assetUrl)
  return assertSchemaAndReturn(assetRaw, AssetSummaryForCreditNoteSchema)
}

export async function getCustomerForCreditNote(params: {
  customerExternalIdentifier: string
  contractFundEntityId: number
}): Promise<CreditNoteCustomer> {
  const { customerExternalIdentifier, contractFundEntityId } = params
  const customerUrl = buildApiUrl(
    Type.AssetManagement,
    `customers/${customerExternalIdentifier}/contract-fund-entities/${contractFundEntityId}`
  )
  const { data: customerRaw } = await sendGetRequest(customerUrl)
  return assertSchemaAndReturn(customerRaw, customerSchema)
}

export async function getAssetVat(assetCode: string): Promise<AssetVat> {
  const url = buildApiUrl(Type.AssetManagement, `assets/${assetCode}/billing/vat`)
  const { data: vatRaw } = await sendGetRequest(url)
  return assertSchemaAndReturn(vatRaw, AssetVatSchema)
}

export async function getCreditNoteById(id: number): Promise<CreateCreditNoteResponse> {
  const url = buildApiUrl(Type.AssetManagement, `billing/credit-notes/${id}`)
  const { data } = await sendGetRequest(url)
  return assertSchemaAndReturn(data, CreateCreditNoteResponseSchema)
}

export async function createCreditNote(req: CreateCreditNoteRequest): Promise<CreateCreditNoteResponse> {
  assertSchema(req, CreateCreditNoteRequestSchema)

  const url = buildApiUrl(Type.AssetManagement, 'billing/credit-notes')
  const { data } = await sendPostRequest<CreateCreditNoteResponse, { data: CreateCreditNoteRequest }>(url, {
    data: req
  })

  return assertSchemaAndReturn(data, CreateCreditNoteResponseSchema)
}

export async function updateCreditNote(req: UpdateCreditNoteRequest): Promise<UpdateCreditNoteResponse> {
  assertSchema(req, UpdateCreditNoteRequestSchema)

  const url = buildApiUrl(Type.AssetManagement, `billing/credit-notes/${req.id}`)
  const { data } = await sendPatchRequest<UpdateCreditNoteResponse, { data: UpdateCreditNoteRequest }>(url, {
    data: req
  })

  return assertSchemaAndReturn(data, UpdateCreditNoteResponseSchema)
}

export async function deleteCreditNoteItemById(creditNoteId: number, itemId: number): Promise<void> {
  const url = buildApiUrl(Type.AssetManagement, `billing/credit-notes/${creditNoteId}/items/${itemId}`)
  await sendDeleteRequest(url)
}

export async function getAssetDetails(assetCode: string): Promise<GetAssetDetailsResponse> {
  const assetUrl = buildApiUrl(Type.AssetManagement, `assets/${assetCode}`)
  const { data: assetRaw } = await sendGetRequest(assetUrl)
  return assertSchemaAndReturn(assetRaw, AssetSummaryForCreditNoteSchema)
}

export async function approveCreditNoteById(creditNoteId: number): Promise<CreateCreditNoteResponse> {
  const url = buildApiUrl(Type.AssetManagement, `billing/credit-notes/${creditNoteId}/statuses/approve`)
  const { data } = await sendPatchRequest<CreateCreditNoteResponse, { data: {} }>(url, { data: {} })
  return assertSchemaAndReturn(data, CreateCreditNoteResponseSchema)
}

export async function cancelCreditNoteById(creditNoteId: number): Promise<CreateCreditNoteResponse> {
  const url = buildApiUrl(Type.AssetManagement, `billing/credit-notes/${creditNoteId}/statuses/cancel`)
  const { data } = await sendPatchRequest<CreateCreditNoteResponse, { data: {} }>(url, { data: {} })
  return assertSchemaAndReturn(data, CreateCreditNoteResponseSchema)
}

export async function sendCreditNoteById(creditNoteId: number) {
  const url = buildApiUrl(Type.AssetManagement, `billing/credit-notes/${creditNoteId}/send`)
  await sendPostRequest(url)
}
export async function regenerateCreditNote(creditNoteId: number) {
  const url = buildApiUrl(Type.AssetManagement, `billing/credit-notes/${creditNoteId}/regenerate`)
  await sendPostRequest(url)
}

export async function recreateCreditNote(creditNoteId: number) {
  const url = buildApiUrl(Type.AssetManagement, `billing/credit-notes/${creditNoteId}/recreate`)
  await sendPostRequest(url)
}

